/**
 * documentEnhancer — turns raw phone photos into scanner-quality captures.
 *
 * We use jscanify (a thin wrapper over OpenCV.js that ships handy
 * highlightPaper/extractPaper helpers) to detect the four corners of
 * the document in the frame and apply a perspective warp so the result
 * looks like a flat-bed scan even when the phone was slightly tilted.
 *
 * Both OpenCV.js (~9.5 MB) and jscanify (~5 KB) are loaded from a public
 * CDN *lazily* — only the first time someone actually captures a page.
 * This keeps the main app bundle small.  Subsequent calls resolve the
 * cached promise immediately.
 *
 * Failure mode is graceful: if OpenCV.js can't load (offline iPad in
 * a room with bad Wi-Fi) or if jscanify can't find document corners
 * (e.g. very low-contrast page on a wooden desk), we fall back to the
 * raw photo. The scanner still works end-to-end.
 */

const OPENCV_CDN = 'https://docs.opencv.org/4.9.0/opencv.js';
const JSCANIFY_CDN = 'https://cdn.jsdelivr.net/npm/jscanify@1.2.0/src/jscanify.min.js';

// Singletons — one download per browser session, cached in module scope.
let opencvPromise = null;
let jscanifyPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    // If already present in DOM, resolve immediately.
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function loadOpenCV() {
  if (opencvPromise) return opencvPromise;
  opencvPromise = new Promise((resolve, reject) => {
    // OpenCV.js signals "ready" via window.cv.onRuntimeInitialized
    if (window.cv && window.cv.Mat) return resolve(window.cv);
    loadScript(OPENCV_CDN)
      .then(() => {
        // The script exposes `cv` but Wasm may still be initialising.
        const start = Date.now();
        const check = () => {
          if (window.cv && window.cv.Mat) return resolve(window.cv);
          if (Date.now() - start > 20000) return reject(new Error('OpenCV.js runtime timeout'));
          if (window.cv && typeof window.cv.onRuntimeInitialized !== 'undefined' && !window.cv.Mat) {
            // Hook the completion callback
            window.cv.onRuntimeInitialized = () => resolve(window.cv);
            return;
          }
          setTimeout(check, 100);
        };
        check();
      })
      .catch(reject);
  });
  return opencvPromise;
}

function loadJscanify() {
  if (jscanifyPromise) return jscanifyPromise;
  jscanifyPromise = loadOpenCV().then(() =>
    loadScript(JSCANIFY_CDN).then(() => {
      if (!window.jscanify) throw new Error('jscanify not exposed on window');
      return new window.jscanify();
    })
  );
  return jscanifyPromise;
}

/**
 * Kick off the loading in the background so the first "Guardar como PDF"
 * doesn't wait 20s. Safe to call multiple times.
 */
export function warmUpDocumentEnhancer() {
  loadJscanify().catch(() => { /* offline is fine */ });
}

/**
 * Given a data URL of a phone photo, returns a *new* data URL where
 * the document has been auto-cropped + perspective-corrected to a flat
 * A-page rectangle.  Falls back to the original data URL if the
 * enhancement fails (offline, low-contrast, etc.).
 *
 * @param {string} dataUrl  original JPEG/PNG data URL
 * @returns {Promise<{dataUrl: string, enhanced: boolean}>}
 */
// Hard cap on how long we let the WASM pipeline run before falling back
// to the raw photo.  Real numbers from the field: iPhone 12 Safari takes
// ~2-4s for the first page after a cold OpenCV load; older iPads and
// low-end Android easily hit 15-20s.  If the enhance step exceeds this
// budget we surrender and keep the original photo — better a slightly
// tilted document than a UI stuck on "mejorando".
const ENHANCE_TIMEOUT_MS = 8000;

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label}_timeout`)), ms);
    promise.then((v) => { clearTimeout(t); resolve(v); },
                 (e) => { clearTimeout(t); reject(e); });
  });
}

export async function enhanceDocument(dataUrl) {
  try {
    const scanner = await withTimeout(loadJscanify(), ENHANCE_TIMEOUT_MS, 'jscanify_load');
    const img = await loadImage(dataUrl);

    // Skip enhancement on tiny images — probably a UI icon, not a doc.
    if (img.naturalWidth < 400 || img.naturalHeight < 400) {
      return { dataUrl, enhanced: false };
    }

    // jscanify's extractPaper expects a canvas or image + target size.
    // Compute the target size based on the *estimated* aspect ratio of
    // the detected paper contour, capped at a max width so the PDF
    // pages don't balloon to 12 MB each.  We aim for ~1600 px on the
    // longest side which is more than enough resolution for OCR and
    // still keeps file size reasonable.
    const src = document.createElement('canvas');
    src.width = img.naturalWidth;
    src.height = img.naturalHeight;
    src.getContext('2d').drawImage(img, 0, 0);

    // Use jscanify.extractPaper to warp. It accepts (image, resultWidth, resultHeight).
    // A4 aspect ~1:1.414 → keep the longest side at 1600 and derive the shorter.
    const isLandscape = img.naturalWidth > img.naturalHeight;
    const longSide = 1600;
    const shortSide = Math.round(longSide / 1.414);
    const targetW = isLandscape ? longSide : shortSide;
    const targetH = isLandscape ? shortSide : longSide;

    const warpedCanvas = await withTimeout(
      Promise.resolve().then(() => scanner.extractPaper(src, targetW, targetH)),
      ENHANCE_TIMEOUT_MS,
      'extract_paper'
    );
    if (!warpedCanvas) return { dataUrl, enhanced: false };

    // Return JPEG at high quality to preserve legibility, but not lossless
    // (halves file size vs PNG for the same visual output).
    return {
      dataUrl: warpedCanvas.toDataURL('image/jpeg', 0.9),
      enhanced: true,
    };
  } catch (err) {
    // Never break the flow — always give back *something* usable.
    // Common causes: offline, jscanify couldn't find 4 corners, iPad Safari
    // WASM restrictions.  Log for triage but don't toast — the user has a
    // manual "Original" toggle if the enhancement misfires.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('enhanceDocument fallback:', err.message || err);
    }
    return { dataUrl, enhanced: false };
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}
